import { uiPaper } from "shared/ui/uiSurface"
import { FilterList, Inbox, SearchOff } from "@mui/icons-material"
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  Fab,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import dayjs, { Dayjs } from "dayjs"
import { GridPaginationModel } from "@mui/x-data-grid"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useUiDensity } from "../../hooks/useUiDensity"
import {
  DashboardOrder,
  useGetDashboardCountsQuery,
  useGetDashboardOrdersQuery,
  useLazyGetExportOrdersQuery,
} from "../../apis/appApi"
import { useReportExport } from "../reports/useReportExport"
import { OrderActionBar } from "./OrderActionBar"
import { OrderCreateDialog } from "./create/OrderCreateDialog"
import { OrdersFilterPanel } from "./OrdersFilterPanel"
import { OrdersGrid } from "./OrdersGrid"
import {
  dashboardOrderToLabel,
  LabelData,
  PrintLabelView,
  triggerPrint,
} from "./PrintLabelView"
import { ProviderOrderMap } from "./ProviderOrderMap"
import {
  actionButtons,
  stateToGroupKey,
  statusGroups,
} from "./ordersPageConfig"

export const OrdersPage: FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const { tokens } = useUiDensity()

  // State
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs())
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null)
  const [search, setSearch] = useState("")
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([])
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [viewMode, setViewMode] = useState<"grid" | "map">("grid")
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  })

  // Data
  const deliveryDateStr = selectedDate.format("YYYY-MM-DD")
  const { data: countsResponse, refetch: refetchCounts } =
    useGetDashboardCountsQuery({ deliveryDate: deliveryDateStr })

  // Derive group counts from the dedicated counts endpoint
  const groupCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const group of statusGroups) {
      counts[group.key] = 0
    }
    if (countsResponse?.byState) {
      for (const [state, count] of Object.entries(countsResponse.byState)) {
        const groupKey = stateToGroupKey[state]
        if (groupKey) {
          counts[groupKey] = (counts[groupKey] || 0) + count
        }
      }
    }
    return counts
  }, [countsResponse])

  // Auto-select first group with count > 0 if nothing selected
  const effectiveGroup = useMemo(() => {
    if (selectedGroup) return selectedGroup
    const first = statusGroups.find((g) => (groupCounts[g.key] ?? 0) > 0)
    return first?.key ?? statusGroups[0].key
  }, [selectedGroup, groupCounts])

  // Resolve states for the active group — sent to backend for server-side filtering
  const activeGroupStates = useMemo(() => {
    const group = statusGroups.find((g) => g.key === effectiveGroup)
    return group?.states ?? []
  }, [effectiveGroup])

  // Server-side paginated + state-filtered query
  const {
    data: response,
    isLoading,
    isFetching,
    refetch,
  } = useGetDashboardOrdersQuery({
    deliveryDate: deliveryDateStr,
    page: paginationModel.page + 1, // DataGrid is 0-based, backend is 1-based
    pageSize: paginationModel.pageSize,
    states: activeGroupStates,
  })

  const orders = response?.data ?? []
  const serverTotal = response?.pagination?.total ?? 0

  // Client-side search filter (on top of server-side state filter)
  const filteredOrders = useMemo(() => {
    if (!search.trim()) return orders
    const term = search.toLowerCase()
    return orders.filter((o) => matchesSearch(o, term))
  }, [orders, search])

  // Check if the active group supports multi-select actions
  const hasMultiSelect = useMemo(() => {
    return actionButtons.some(
      (a) => a.multiSelect && a.visibleForGroups.includes(effectiveGroup),
    )
  }, [effectiveGroup])

  const groupLabel = useMemo(() => {
    const group = statusGroups.find((g) => g.key === effectiveGroup)
    return group ? t(group.labelKey) : ""
  }, [effectiveGroup, t])

  const groupColor = useMemo(() => {
    const group = statusGroups.find((g) => g.key === effectiveGroup)
    return group?.color ?? "#757575"
  }, [effectiveGroup])

  const handleSelectGroup = useCallback((key: string) => {
    setSelectedGroup(key)
    setSelectedOrderIds([])
    setPaginationModel((m) => ({ ...m, page: 0 }))
  }, [])

  const handleDateChange = useCallback((d: Dayjs | null) => {
    if (d) {
      setSelectedDate(d)
      setSelectedGroup(null)
      setSelectedOrderIds([])
      setPaginationModel((m) => ({ ...m, page: 0 }))
    }
  }, [])

  // Filter selected IDs to only include orders visible in the current group
  const validSelectedIds = useMemo(() => {
    const visibleIds = new Set(filteredOrders.map((o) => o.id))
    return selectedOrderIds.filter((id) => visibleIds.has(id))
  }, [selectedOrderIds, filteredOrders])

  // --- Export logic ---
  const [triggerExport] = useLazyGetExportOrdersQuery()
  const { exportToExcel } = useReportExport()
  const [isExporting, setIsExporting] = useState(false)

  const exportColumns = useMemo(
    () => [
      { field: "reference", headerName: t("ordersPage.export.col.reference") },
      { field: "state", headerName: t("ordersPage.export.col.state") },
      { field: "workflow", headerName: t("ordersPage.export.col.workflow"), valueGetter: (r: DashboardOrder) => r.stateMachineWorkflow },
      { field: "deliveryDate", headerName: t("ordersPage.export.col.deliveryDate") },
      { field: "desiredArrivalTime", headerName: t("ordersPage.export.col.desiredArrival") },
      { field: "amount", headerName: t("ordersPage.export.col.amount") },
      { field: "source", headerName: t("ordersPage.export.col.source") },
      { field: "clientName", headerName: t("ordersPage.export.col.client"), valueGetter: (r: DashboardOrder) => r.client ? `${r.client.lastName} ${r.client.firstName}` : "" },
      { field: "address", headerName: t("ordersPage.export.col.address"), valueGetter: (r: DashboardOrder) => r.address ? `${r.address.streetNumber} ${r.address.streetName}, ${r.address.city} ${r.address.postcode}` : "" },
      { field: "tags", headerName: t("ordersPage.export.col.tags"), valueGetter: (r: DashboardOrder) => (r.tags ?? []).map((t) => t.tag?.label).filter(Boolean).join(", ") },
      { field: "notes", headerName: t("ordersPage.export.col.notes") },
      { field: "dateCreated", headerName: t("ordersPage.export.col.dateCreated") },
    ],
    [t],
  )

  const handleExport = useCallback(async () => {
    setIsExporting(true)
    try {
      const params: { deliveryDate: string; orderIds?: number[] } = { deliveryDate: deliveryDateStr }
      // F066: when orders are selected, export only those
      if (validSelectedIds.length > 0) {
        params.orderIds = validSelectedIds
      }
      const result = await triggerExport(params).unwrap()
      const filename = validSelectedIds.length > 0
        ? `orders-${deliveryDateStr}-selected`
        : `orders-${deliveryDateStr}`
      exportToExcel(result.data, exportColumns, filename)
    } catch {
      // Silently fail — the user sees the spinner stop
    } finally {
      setIsExporting(false)
    }
  }, [triggerExport, deliveryDateStr, exportToExcel, exportColumns, validSelectedIds])

  // --- Print labels logic ---
  const [printLabels, setPrintLabels] = useState<LabelData[]>([])

  const handlePrintLabels = useCallback(() => {
    const selectedSet = new Set(validSelectedIds)
    const selectedOrders = filteredOrders.filter((o) => selectedSet.has(o.id))
    if (selectedOrders.length === 0) return
    const labels = selectedOrders.map(dashboardOrderToLabel)
    setPrintLabels(labels)
    // Trigger print in next tick so the labels render first
    requestAnimationFrame(() => {
      triggerPrint()
    })
  }, [validSelectedIds, filteredOrders])

  return (
    <>
    {/* Print labels (hidden on screen, shown on print) */}
    {printLabels.length > 0 && <PrintLabelView labels={printLabels} />}

    <Box
      data-testid="orders-page"
      className="no-print"
      sx={{
        display: "flex",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Left sidebar -- desktop inline, mobile drawer */}
      {isMobile ? (
        <Drawer
          anchor="left"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          slotProps={{ paper: { ...uiPaper("UI-DLV2-022"), sx: { width: tokens.sidebarWidth } } }}
        >
          <OrdersFilterPanel
            date={selectedDate}
            onDateChange={handleDateChange}
            groups={statusGroups}
            groupCounts={groupCounts}
            selectedGroup={effectiveGroup}
            onSelectGroup={(key) => {
              handleSelectGroup(key)
              setFilterDrawerOpen(false)
            }}
            search={search}
            onSearchChange={setSearch}
            onClose={() => setFilterDrawerOpen(false)}
            onCreateOrder={() => setCreateDialogOpen(true)}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </Drawer>
      ) : (
        <OrdersFilterPanel
          date={selectedDate}
          onDateChange={handleDateChange}
          groups={statusGroups}
          groupCounts={groupCounts}
          selectedGroup={effectiveGroup}
          onSelectGroup={handleSelectGroup}
          search={search}
          onSearchChange={setSearch}
          onCreateOrder={() => setCreateDialogOpen(true)}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
      )}

      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <OrderActionBar
          filteredCount={filteredOrders.length}
          groupCount={groupCounts[effectiveGroup] ?? filteredOrders.length}
          groupLabel={groupLabel}
          actions={actionButtons}
          selectedGroup={effectiveGroup}
          selectedOrderIds={validSelectedIds}
          onRefresh={useCallback(() => { refetch(); refetchCounts(); }, [refetch, refetchCounts])}
          onClearSelection={useCallback(() => setSelectedOrderIds([]), [])}
          selectedDate={selectedDate}
          totalDayCount={countsResponse?.total ?? 0}
          groupColor={groupColor}
          onCreateOrder={() => setCreateDialogOpen(true)}
          isMobile={isMobile}
          onExport={handleExport}
          isExporting={isExporting}
          onPrintLabels={handlePrintLabels}
        />

        <Box sx={{ flex: 1, overflow: "hidden" }}>
          {viewMode === "map" ? (
            <ProviderOrderMap
              orders={orders}
              selectedGroup={effectiveGroup}
            />
          ) : isLoading ? (
            <Box
              data-testid="orders-loading"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : filteredOrders.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              {serverTotal === 0 ? (
                <Stack
                  data-testid="orders-empty-state"
                  alignItems="center"
                  spacing={2}
                  sx={{ maxWidth: 360 }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      bgcolor: "grey.100",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Inbox sx={{ fontSize: 36, color: "grey.400" }} />
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="text.primary"
                  >
                    {t("ordersPage.emptyDay.title")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    {t("ordersPage.emptyDay.description", {
                      date: selectedDate.format("DD/MM/YYYY"),
                    })}
                  </Typography>
                  <Button
                    data-testid="orders-empty-create-btn"
                    variant="contained"
                    size="small"
                    onClick={() => setCreateDialogOpen(true)}
                  >
                    {t("orders.create")}
                  </Button>
                </Stack>
              ) : (
                <Stack
                  data-testid="orders-empty-filter"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ maxWidth: 320 }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: "grey.100",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SearchOff sx={{ fontSize: 28, color: "grey.400" }} />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color="text.primary"
                  >
                    {t("ordersPage.emptyFilter.title")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    {t("ordersPage.emptyFilter.description")}
                  </Typography>
                </Stack>
              )}
            </Box>
          ) : (
            <OrdersGrid
              orders={filteredOrders}
              loading={isLoading || isFetching}
              checkboxSelection={hasMultiSelect}
              selectedOrderIds={validSelectedIds}
              onSelectionChange={setSelectedOrderIds}
              rowCount={serverTotal}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
          )}
        </Box>
      </Box>

      {/* Filter FAB -- mobile only */}
      {isMobile && (
        <Fab
          data-testid="orders-filter-fab"
          color="primary"
          size="medium"
          onClick={() => setFilterDrawerOpen(true)}
          sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1100 }}
        >
          <FilterList />
        </Fab>
      )}

      {/* Create order dialog */}
      <OrderCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </Box>
    </>
  )
}

function matchesSearch(order: DashboardOrder, term: string): boolean {
  const fields = [
    order.reference,
    order.client ? `${order.client.lastName} ${order.client.firstName}` : "",
    order.address
      ? `${order.address.streetNumber} ${order.address.streetName} ${order.address.city}`
      : "",
    order.notes,
  ]
  return fields.some((f) => f && f.toLowerCase().includes(term))
}
